'use client';

import { useId, useMemo, useState, type FocusEvent } from 'react';
import { resolveIcon } from './AuthFormInputIcons';

type UseAuthFormInputOptions = {
  id?: string;
  name?: string;
  placeholder?: string;
  value?: unknown;
  type?: string;
  disableFloatingLabel?: boolean;
  disableIcon?: boolean;
  icon?: React.ReactNode;
  errorText?: string;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
};

type AuthFormInputState = {
  inputId: string;
  errorId?: string;
  icon: React.ReactNode;
  showFloatingLabel: boolean;
  isFocused: boolean;
  hasValue: boolean;
};

type AuthFormInputHandlers = {
  handleFocus: (event: FocusEvent<HTMLInputElement>) => void;
  handleBlur: (event: FocusEvent<HTMLInputElement>) => void;
};

export const isValuePresent = (value: unknown) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'number') return true;
  return value !== '';
};

export const useAuthFormInput = (
  options: UseAuthFormInputOptions
): AuthFormInputState & AuthFormInputHandlers => {
  const {
    id,
    name,
    placeholder,
    value,
    type,
    disableFloatingLabel,
    disableIcon,
    icon: iconOverride,
    errorText,
    onFocus,
    onBlur
  } = options;

  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const errorId = errorText ? `${inputId}-error` : undefined;
  const [isFocused, setIsFocused] = useState(false);

  const hasValue = useMemo(() => isValuePresent(value), [value]);
  const icon = disableIcon ? null : (iconOverride ?? resolveIcon(type));
  const showFloatingLabel = Boolean(
    !disableFloatingLabel && placeholder && (isFocused || hasValue)
  );

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  return {
    inputId,
    errorId,
    icon,
    showFloatingLabel,
    isFocused,
    hasValue,
    handleFocus,
    handleBlur
  };
};
